/** Needed for @i18n/next */
"use client";

import type { ReactNode } from "react";
import type {
  Formats,
  MarkupTranslationValues,
  RichTranslationValues,
  TranslationValues,
} from "use-intl";
import { useTranslations as useMessageAgnosticTranslations } from "use-intl";

import type {
  LooseMessageKey,
  MessageNamespace,
  ScopedMessageKey,
} from "../types/appMessages.types";

/** What `useTranslations` returns, scoped to the messages of the app. */
export type AppTranslator<TNamespace extends MessageNamespace = never> = {
  <TKey extends LooseMessageKey<TNamespace>>(
    key: TKey & ScopedMessageKey<TKey, TNamespace>,
    values?: TranslationValues,
    formats?: Partial<Formats>,
  ): string;

  has<TKey extends LooseMessageKey<TNamespace>>(
    key: TKey & ScopedMessageKey<TKey, TNamespace>,
  ): boolean;

  markup<TKey extends LooseMessageKey<TNamespace>>(
    key: TKey & ScopedMessageKey<TKey, TNamespace>,
    values?: MarkupTranslationValues,
    formats?: Partial<Formats>,
  ): string;

  raw<TKey extends LooseMessageKey<TNamespace>>(
    key: TKey & ScopedMessageKey<TKey, TNamespace>,
  ): any;

  rich<TKey extends LooseMessageKey<TNamespace>>(
    key: TKey & ScopedMessageKey<TKey, TNamespace>,
    values?: RichTranslationValues,
    formats?: Partial<Formats>,
  ): ReactNode;
};

/**
 * Overloads rather than one optional parameter, so that the common
 * `ReturnType<typeof useTranslations>` annotation resolves to the unnamespaced
 * translator. A single signature would resolve `TNamespace` to its constraint —
 * every namespace at once — instead of to its default.
 */
export type UseAppTranslations = {
  <TNamespace extends MessageNamespace>(
    namespace: TNamespace,
  ): AppTranslator<TNamespace>;
  (): AppTranslator;
};

/**
 * The same hook `@i18n/react` exports, with its keys narrowed to the messages
 * the app declared through `AppMessagesConfig`. Only the types differ, so the
 * cast stays confined to this line.
 */
export const useTranslations =
  useMessageAgnosticTranslations as UseAppTranslations;
